import { FunnelIcon, PlusIcon } from '@heroicons/react/24/outline';
import { isEqual } from 'lodash';
import { FC, useState, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useNavigate } from 'hotel/navigation';

import {
  DateRangeInput,
  RosterFilterInput,
  RosterRoomAssignmentStatus,
} from '../../generated/graphql';
import { Button, TealButtonSm } from '../Button';
import DateRangeFilterChip from '../Chips/DateRangeFilterChip';
import FilterChip from '../Chips/FilterChip';
import { TableFilterTextField } from '../Inputs';

interface FilterChipsContainerProps {
  filters: Partial<RosterFilterInput>;
  onUpdateFilter: (values: Partial<RosterFilterInput>) => void;
  onSubmit: (values: Partial<RosterFilterInput>) => void;
  openSidebar: () => void;
}

const defaultStatusValues = [
  RosterRoomAssignmentStatus.InHouse,
  RosterRoomAssignmentStatus.Upcoming,
];

const FilterChipsContainer: FC<FilterChipsContainerProps> = ({
  filters,
  onSubmit,
  openSidebar,
}) => {
  const navigate = useNavigate();
  const [activeChip, setActiveChip] = useState<string | null>(null);
  const [tempFilters, setTempFilters] =
    useState<Partial<RosterFilterInput>>(filters);

  // Update temp filters when parent filters change
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  const handleChipUpdate = (fieldName: string, value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleDateRangeUpdate = (dateRange: {
    checkInDate?: DateRangeInput | null;
    checkOutDate?: DateRangeInput | null;
  }) => {
    setTempFilters((prev) => ({
      ...prev,
      ...dateRange,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(tempFilters);
  };

  const handleClearFilters = () => {
    navigate(location.pathname, { replace: true });
    // Mantener los valores por defecto del status
    const defaultFilters = {
      status: defaultStatusValues,
    };
    setTempFilters(defaultFilters);
    onSubmit(defaultFilters);
  };

  return (
    <div className="border border-gray-200 bg-gray-100 p-5 sm:px-5">
      <Form
        onSubmit={onSubmit}
        initialValues={filters}
        initialValuesEqual={(obj1, obj2) => isEqual(obj1, obj2)}
        render={() => (
          <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            {/* Mobile View */}
            <div className="flex items-center justify-between sm:hidden">
              <h1 className="text-xl font-bold text-primaryBlue">
                Live Roster View
              </h1>
              <Button
                type="button"
                onClick={openSidebar}
                className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                Filters
                <FunnelIcon className="size-4" />
              </Button>
            </div>

            {/* Desktop View */}
            <div className="hidden flex-wrap items-center gap-2 p-4 sm:flex">
              <div className="flex items-center">
                <FunnelIcon className="size-4" />
              </div>
              <FilterChip
                label="Guest"
                value={tempFilters.fullName || ''}
                fieldName="fullName"
                inputComponent={TableFilterTextField}
                inputProps={{
                  labelText: 'Guest Name',
                  placeholder: 'Enter guest name',
                }}
                onUpdate={handleChipUpdate}
                isEditing={activeChip === 'fullName'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'fullName' : null)
                }
              />
              <FilterChip
                label="Property"
                value={tempFilters.property || ''}
                fieldName="property"
                inputComponent={TableFilterTextField}
                inputProps={{
                  labelText: 'Property',
                  placeholder: 'Enter hotel or property name',
                }}
                onUpdate={handleChipUpdate}
                isEditing={activeChip === 'property'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'property' : null)
                }
              />
              <DateRangeFilterChip
                value={{
                  checkInDate: tempFilters.checkInDate as DateRangeInput,
                  checkOutDate: tempFilters.checkOutDate as DateRangeInput,
                }}
                onUpdate={handleDateRangeUpdate}
                isEditing={activeChip === 'dateRange'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'dateRange' : null)
                }
              />
              <FilterChip
                label="Confirmation #"
                value={tempFilters.hotelConfirmationNumber || ''}
                fieldName="hotelConfirmationNumber"
                inputComponent={TableFilterTextField}
                inputProps={{
                  labelText: 'Confirmation #',
                  placeholder: 'Enter confirmation #',
                }}
                onUpdate={handleChipUpdate}
                isEditing={activeChip === 'hotelConfirmationNumber'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'hotelConfirmationNumber' : null)
                }
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  type="button"
                  onClick={openSidebar}
                  className="items-center gap-2"
                >
                  Add Filter
                  <PlusIcon className="size-4" />
                </Button>
                <Button size="sm" type="button" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
                <TealButtonSm type="submit">Apply Filters</TealButtonSm>
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default FilterChipsContainer;
